import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import { getFileUrl, updateUserImageUrl, uploadAvatar } from "../../actions";
import { getUserId } from "../../auth";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../ui/Modal";
import { Checkbox, Field, Input, Textarea } from "../ui/Field";
import { Button } from "../ui/Button";

const DEFAULT_DATA = {
  orc_id: "",
  seller_info: {},
  content_info: {},
  user_img: "",
  mstdn_url: "",
  last_name: "",
  first_name: "",
  gitHub_url: "",
  gitlab_url: "",
  profession: "",
  twitter_url: "",
  website_url: "",
  youtube_url: "",
  linkedin_url: "",
  field_of_study: "",
  bio_descriptions: "",
  instagram_handle: "",
  university_email: "",
  media_showcase_upload: {},
  media_showcase_urlembed: "",
};

export default function ProfileInformation({ openDialog, handleCloseDialog, onSubmit }) {
  const dispatch = useDispatch();
  const { user, user_image_url = "" } = useSelector((state) => state.bios);
  const userProfile = (user && user.account_info) || DEFAULT_DATA;

  const [open, setOpen] = useState(false);
  const [optOutSelling, setOptOutSelling] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [data, setData] = useState(userProfile);

  useEffect(() => setOpen(openDialog), [openDialog]);

  const close = () => {
    setOpen(false);
    handleCloseDialog(false);
  };

  const change = (key) => (e) => setData({ ...data, [key]: e.target.value });

  const handleSelectImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Was calling `getUploadToken` (the model-upload flow) without its
    // required `modelId` argument — positionally shifted every later
    // argument, silently breaking the upload (wrong URL, no-op success
    // callback, undefined file body). Avatars have their own dedicated
    // server-proxied endpoint now (ticket 86ak29prz) — no modelId to omit.
    dispatch(
      uploadAvatar(getUserId(), file, async ({ user_img }) => {
        setData({ ...data, user_img });
        const response = await getFileUrl(user_img);
        dispatch(updateUserImageUrl(response.data));
      })
    );
  };

  return (
    <Modal open={open} onClose={close} maxWidth="max-w-2xl">
      <ModalHeader
        title="Profile information"
        subtitle="Build your profile — link the platforms where your work already lives."
        onClose={close}
      />
      <ModalBody className="space-y-6">
        {/* Bio + image */}
        <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-5 items-start">
          <div className="flex flex-col items-center gap-2">
            <div className="w-24 h-24 rounded-full border border-figures-rule dark:border-white/10 bg-figures-wash dark:bg-white/5 overflow-hidden grid place-items-center">
              {user_image_url ? (
                <img src={user_image_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-figures-subtle dark:text-brand-subtleInv text-2xl font-figuresSerif">
                  {(data.first_name?.[0] || "?").toUpperCase()}
                </span>
              )}
            </div>
            <label className="text-xs text-figures-muted dark:text-brand-mutedInv hover:text-figures-ink dark:hover:text-brand-displayInv hover:underline cursor-pointer flex items-center gap-1 transition">
              <UploadIcon sx={{ fontSize: 14 }} /> Upload
              <input type="file" accept="image/*" onChange={handleSelectImage} className="hidden" />
            </label>
          </div>
          <Field label="Bio">
            <Textarea
              rows={4}
              value={data.bio_descriptions}
              onChange={change("bio_descriptions")}
              placeholder="A few sentences about your work."
            />
          </Field>
        </div>

        {/* Identity */}
        <div className="space-y-4">
          <p className="text-xs uppercase tracking-[0.18em] text-figures-muted dark:text-brand-mutedInv mono">
            identity
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Profession">
              <Input value={data.profession} onChange={change("profession")} />
            </Field>
            <Field label="Field of study">
              <Input
                value={data.field_of_study}
                onChange={change("field_of_study")}
                placeholder="Cardiology, etc."
              />
            </Field>
            <Field label="University email">
              <Input
                value={data.university_email}
                onChange={change("university_email")}
                placeholder="you@university.edu"
              />
            </Field>
            <Field label="ORCID iD">
              <Input value={data.orc_id} onChange={change("orc_id")} />
            </Field>
          </div>
        </div>

        {/* Links */}
        <div className="space-y-4">
          <p className="text-xs uppercase tracking-[0.18em] text-figures-muted dark:text-brand-mutedInv mono">
            links
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Personal site">
              <Input value={data.website_url} onChange={change("website_url")} />
            </Field>
            <Field label="LinkedIn">
              <Input value={data.linkedin_url} onChange={change("linkedin_url")} />
            </Field>
            <Field label="Twitter / X">
              <Input value={data.twitter_url} onChange={change("twitter_url")} />
            </Field>
            <Field label="YouTube">
              <Input value={data.youtube_url} onChange={change("youtube_url")} />
            </Field>
            <Field label="Mastodon">
              <Input value={data.mstdn_url} onChange={change("mstdn_url")} />
            </Field>
            <Field label="GitHub">
              <Input value={data.gitHub_url} onChange={change("gitHub_url")} />
            </Field>
            <Field label="GitLab">
              <Input value={data.gitlab_url} onChange={change("gitlab_url")} />
            </Field>
            <Field label="Instagram handle">
              <Input value={data.instagram_handle} onChange={change("instagram_handle")} />
            </Field>
          </div>
        </div>

        {/* Preferences */}
        <div className="space-y-3 pt-2 border-t border-figures-rule dark:border-white/10">
          <Checkbox
            checked={optOutSelling}
            onChange={() => setOptOutSelling((v) => !v)}
            label="I would not like to sign up to sell on the BIOS platform."
          />
          <Checkbox
            checked={isAffiliate}
            onChange={() => setIsAffiliate((v) => !v)}
            label="I would like to be an affiliate of BIOS."
          />
        </div>

        {isAffiliate && (
          <div className="space-y-4 p-4 rounded-md bg-figures-wash dark:bg-white/5 border border-figures-rule dark:border-white/10">
            <p className="text-xs uppercase tracking-[0.18em] text-figures-muted dark:text-brand-mutedInv mono">
              affiliate address
            </p>
            <Field label="Email">
              <Input onChange={change("email")} placeholder="you@lab.edu" />
            </Field>
            <Field label="Street address">
              <Input onChange={change("streetAddressOne")} />
            </Field>
            <Field label="Street address 2">
              <Input onChange={change("streetAddressTwo")} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="City">
                <Input onChange={change("city")} />
              </Field>
              <Field label="Region / State / Province">
                <Input onChange={change("region")} />
              </Field>
              <Field label="Postal / ZIP code">
                <Input onChange={change("postalCode")} />
              </Field>
              <Field label="Country">
                <Input onChange={change("country")} />
              </Field>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => onSubmit(data)} className="w-full">
          Continue to profile
        </Button>
      </ModalFooter>
    </Modal>
  );
}
